import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const UPPER_FIELDS = [
  { key: 'length', label: 'Length (L)' },
  { key: 'upper_chest', label: 'Upper Chest (UCH)' },
  { key: 'chest', label: 'Chest (CH)' },
  { key: 'waist', label: 'Waist (W)' },
  { key: 'hip', label: 'Hip' },
  { key: 'shoulder', label: 'Shoulder (SH)' },
  { key: 'samna', label: 'Samna (Front Cross)' },
  { key: 'armhole', label: 'Armhole (AH)' },
  { key: 'ghera', label: 'Ghera' },
  { key: 'neck', label: 'Neck' },
  { key: 'front', label: 'Front (F)' },
  { key: 'back', label: 'Back (B)' },
  { key: 'sleeves_length', label: 'Sleeves Length' },
  { key: 'sleeves_moore', label: 'Sleeves Moore' },
  { key: 'back_cross', label: 'Back Cross' },
];

const BOTTOM_FIELDS = [
  { key: 'length', label: 'Length (L)' },
  { key: 'waist', label: 'Waist (W)' },
  { key: 'hip', label: 'Hip' },
  { key: 'upper_thigh', label: 'Upper Thigh' },
  { key: 'lower_thigh', label: 'Lower Thigh' },
  { key: 'moore', label: 'Moore' },
];

const BLOUSE_FIELDS = [
  { key: 'length', label: 'Length (L)' },
  { key: 'chest', label: 'Chest (CH)' },
  { key: 'waist', label: 'Waist (W)' },
  { key: 'shoulder', label: 'Shoulder (SH)' },
  { key: 'samna', label: 'Samna (Front Cross)' },
  { key: 'armhole', label: 'Armhole (AH)' },
  { key: 'dart_point', label: 'Dart Point (DP)' },
  { key: 'neck', label: 'Neck' },
  { key: 'front', label: 'Front (F)' },
  { key: 'back', label: 'Back (B)' },
  { key: 'sleeves_length', label: 'Sleeves Length' },
  { key: 'sleeves_moore', label: 'Sleeves Moore' },
];

function MeasurementGrid({ fields, prefix, measurements, onChange }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 sm:gap-x-6 gap-y-3 sm:gap-y-4">
      {fields.map(({ key, label }) => (
        <div key={`${prefix}_${key}`}>
          <Label className="text-[10px] sm:text-sm font-semibold tracking-wide uppercase text-[#8E8E93] mb-1 block">
            {label}
          </Label>
          <Input
            data-testid={`measurement-${prefix}-${key}`}
            className="h-10 sm:h-12 bg-white border-[#E5E5EA] text-[#1C1C1E] placeholder:text-[#C7C7CC] focus:ring-1 focus:ring-[#D1D1D6] rounded-sm text-sm"
            placeholder="--"
            value={measurements[`${prefix}_${key}`] || ''}
            onChange={(e) => onChange(`${prefix}_${key}`, e.target.value)}
          />
        </div>
      ))}
    </div>
  );
}

export function MeasurementForm({ garmentType, measurements, onChange, onGarmentTypeChange }) {
  const defaultTab = garmentType === 'bottom' ? 'bottom' : garmentType === 'blouse' ? 'blouse' : 'upper';

  return (
    <div>
      {onGarmentTypeChange && (
        <div className="mb-4 sm:mb-6">
          <Label className="text-[10px] sm:text-sm font-semibold tracking-wide uppercase text-[#8E8E93] mb-1 sm:mb-1.5 block">
            Garment Type
          </Label>
          <Select value={garmentType} onValueChange={onGarmentTypeChange}>
            <SelectTrigger data-testid="garment-type-select" className="h-10 sm:h-12 border-[#E5E5EA] rounded-sm text-sm">
              <SelectValue placeholder="Select garment type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="upper">Upper (Suit/Kurta)</SelectItem>
              <SelectItem value="bottom">Bottom (Pants/Salwar)</SelectItem>
              <SelectItem value="blouse">Blouse</SelectItem>
              <SelectItem value="upper_bottom">Upper + Bottom Set</SelectItem>
              <SelectItem value="full_set">Full Set (Upper + Bottom + Blouse)</SelectItem>
              <SelectItem value="lehenga">Lehenga</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="flex space-x-1 border-b border-[#E5E5EA] mb-6 overflow-x-auto bg-transparent p-0 h-auto">
          {(garmentType === 'upper' || garmentType === 'upper_bottom' || garmentType === 'full_set' || garmentType === 'lehenga') && (
            <TabsTrigger
              value="upper"
              data-testid="tab-upper"
              className="px-4 py-2.5 text-sm font-medium text-[#8E8E93] hover:text-[#1C1C1E] data-[state=active]:text-[#1C1C1E] data-[state=active]:border-b-2 data-[state=active]:border-[#C27185] data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none transition-all"
            >
              Upper
            </TabsTrigger>
          )}
          {(garmentType === 'bottom' || garmentType === 'upper_bottom' || garmentType === 'full_set') && (
            <TabsTrigger
              value="bottom"
              data-testid="tab-bottom"
              className="px-4 py-2.5 text-sm font-medium text-[#8E8E93] hover:text-[#1C1C1E] data-[state=active]:text-[#1C1C1E] data-[state=active]:border-b-2 data-[state=active]:border-[#C27185] data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none transition-all"
            >
              Bottom
            </TabsTrigger>
          )}
          {(garmentType === 'blouse' || garmentType === 'full_set' || garmentType === 'lehenga') && (
            <TabsTrigger
              value="blouse"
              data-testid="tab-blouse"
              className="px-4 py-2.5 text-sm font-medium text-[#8E8E93] hover:text-[#1C1C1E] data-[state=active]:text-[#1C1C1E] data-[state=active]:border-b-2 data-[state=active]:border-[#C27185] data-[state=active]:bg-transparent data-[state=active]:shadow-none rounded-none transition-all"
            >
              Blouse
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="upper">
          <MeasurementGrid fields={UPPER_FIELDS} prefix="upper" measurements={measurements} onChange={onChange} />
        </TabsContent>
        <TabsContent value="bottom">
          <MeasurementGrid fields={BOTTOM_FIELDS} prefix="bottom" measurements={measurements} onChange={onChange} />
        </TabsContent>
        <TabsContent value="blouse">
          <MeasurementGrid fields={BLOUSE_FIELDS} prefix="blouse" measurements={measurements} onChange={onChange} />
          <div className="mt-4">
            <Label className="text-sm font-semibold tracking-wide uppercase text-[#8E8E93] mb-1.5 block">Opening</Label>
            <Select
              value={measurements.blouse_opening || ''}
              onValueChange={(v) => onChange('blouse_opening', v)}
            >
              <SelectTrigger data-testid="blouse-opening-select" className="h-12 border-[#E5E5EA] rounded-sm">
                <SelectValue placeholder="Select opening type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="side_zip">Side Zip</SelectItem>
                <SelectItem value="front">Front</SelectItem>
                <SelectItem value="back">Back</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
